const axios = require('axios');
const parseXml = require('./parse-xml');
const CancelToken = axios.CancelToken;


/**
 * Class for doing ajax requests
 *
 * This is a wrapper axios ajax library
 *
 * It adds retry functionality that can handle loss of network connection and
 * has better error handling than the standard fetch API.
 *
 * Usage:
 *       var Ajax = require('./ajax');
 *
 *       Ajax.get({
 *       url: "http://localhost/userdata.json",
 *       responseType: 'JSON'
 *        retry: {
 *          times: 5,
 *           waitTimeBetweenRetries: 1000,
 *            statusCodes: [500]
 *           }
 *      }).then ((json) => {
 *         alert("UserId: " + json.userId);
 *      }).catch((error) => {
 *         console.error("something went wrong with the ajax request", error);
 *      });
 *
 * Options:
 *  - responseType (required)
 *    This can be 'XML', 'JSON', 'TEXT', 'IMAGE', 'BLOB'
 *    If you specify XML you will get an XML document as result
 *    If you specify JSON you will get an JSON object as result
 *    If you specify TEXT you will get a plain text string as result
 *    If you specify IMAGE you will get an object url to an image as a result (that you can put in img src)
 *    If you specify BLOB you will get a Blob object
 *    If it cannot give you what you asked, you will get an error message instead
 *  - retry (optional)
 *    An object that specifies when to retry failed requests. It has the following possible parameters:
 *    - times (how many times to you want it to retry before giving up?)
 *    - waitTimeBetweenRetries (how many milliseconds to wait before trying again)
 *    - statusCodes (an array of status codes to trigger a retry)
 *    - regexMatchResponse (a regex to match a response that will trigger a retry)
 *    - network (an object defining retries on network specific errors)
 *      - times
 *      - waitTimeBetweenRetries
 *  - headers (optional)
 *    An object with key:value pairs where you specify additional headers for the request
 *  - body (for POST and PUT requests)
 *    This can be any of the following:
 *    - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams, FormData, File, Blob
 */

const Ajax = {
    defaults: {
        ERROR_RETRY_COUNT: 0,
        ERROR_RETRY_TIMEOUT: 1000,
        NETWORK_ERROR_RETRY_COUNT: 500,
        NETWORK_ERROR_RETRY_TIMEOUT: 5000,
        VALID_RESPONSE_TYPES: ['XML', 'JSON', 'TEXT', 'IMAGE', 'BLOB', 'NONE'],
        MANDATORY_BODY_METHODS: ['POST', 'PUT']
    }
};

/**
 * Get
 * @param options
 * @returns {Promise<any>}
 */
Ajax.get = (options) => {
    Ajax._validateOptions(options, 'GET');
    return new Promise((resolve, reject) => {
        Ajax._fetch(options.url, 'GET', options, resolve, reject);
    });
};

/**
 * Post
 * @param options
 * @returns {Promise<any>}
 */
Ajax.post = (options) => {
    Ajax._validateOptions(options, 'POST');
    return new Promise((resolve, reject) => {
        Ajax._fetch(options.url, 'POST', options, resolve, reject);
    });
};

/**
 * Put
 * @param options
 * @returns {Promise<any>}
 */
Ajax.put = (options) => {
    Ajax._validateOptions(options, 'PUT');
    return new Promise((resolve, reject) => {
        Ajax._fetch(options.url, 'PUT', options, resolve, reject);
    });
};

/**
 * Delete
 * @param options
 * @returns {Promise<any>}
 */
Ajax.delete = (options) => {
    Ajax._validateOptions(options, 'DELETE');
    return new Promise((resolve, reject) => {
        Ajax._fetch(options.url, 'DELETE', options, resolve, reject);
    });
};

/**
 * Get cancellation token
 * @returns {*}
 */
Ajax.getCancelToken = () => CancelToken.source();

/*** PRIVATE METHODS ***/

/**
 * Fetch
 * @param {string} url
 * @param {string} method
 * @param {Object} options
 * @param resolve
 * @param reject
 * @param {number} retryCount
 * @private
 */
Ajax._fetch = (url, method, options, resolve, reject, retryCount = 0) => {

    const requestOptions = {
        method: method,
        url: url
    };
    if (options.body) {
        requestOptions.data = options.body;
    }
    if (options.headers) {
        requestOptions.headers = options.headers;
    }
    if (options.responseType === 'IMAGE') {
        requestOptions.responseType = 'blob';
    }
    if (typeof options.timeout === "number") {
        requestOptions.timeout = options.timeout;
    }
    if (options.cancelToken) {
        requestOptions.cancelToken = options.cancelToken;
    }
    if (options.onUploadProgress) {
        requestOptions.onUploadProgress = options.onUploadProgress;
    }

    requestOptions.validateStatus = (status) => (status >= 200); // Most statuses will not throw axios error, but will be validated by this class instead

    axios(requestOptions)
        .then((response) => {
            Ajax._validateResponse(response, options);
            Ajax._handleResponseData(response, options, resolve);
        })
        .catch((error) => {

            let responseText;
            if (error.response && error.response.data) {
                responseText = error.response.data;
            }

            // If request was canceled
            if (axios.isCancel(error)) {
                reject({
                    message: "Unable to complete ajax request",
                    subMessage: "Cancelled",
                    url: url,
                    method: method,
                    options: JSON.stringify(options),
                    status: status,
                    error: JSON.stringify(error),
                    retryCount: retryCount,
                    responseText: responseText
                });
                return;
            }

            // If trying to upload an empty file. This could happen if we lost the file reference due to computer going into sleep mode
            if (requestOptions.data instanceof File && requestOptions.data.size === 0) {
                reject({
                    message: "Unable to complete ajax request",
                    subMessage: "Empty File",
                    url: url,
                    method: method,
                    options: JSON.stringify(options),
                    status: status,
                    error: JSON.stringify(error),
                    retryCount: retryCount,
                    responseText: responseText
                });
                return;
            }

            const maxRetries = (options.retry && options.retry.times) ? options.retry.times : Ajax.defaults.ERROR_RETRY_COUNT;

            let retry = (retryCount < maxRetries);

            let timeout = (options.retry && options.retry.waitTimeBetweenRetries) ? options.retry.waitTimeBetweenRetries : Ajax.defaults.ERROR_RETRY_TIMEOUT;

            // Increment timeout for exponential backoff TODO: Something more elegant
            if (options.retry && options.retry.exponentialBackoff) {
                if (retryCount >= 3 && retryCount <= 10) {
                    timeout = timeout * 4;
                }
                if (retryCount >= 11) {
                    timeout = timeout * 10;
                }
            }

            // If error is network related
            if ((error.response && !error.response.status) || (error.message &&
                    error.message.indexOf("Network Error") > -1) || (
                    error.code && error.code === 'ECONNABORTED')) {
                
                // if the error is due to dropped internet connection, we will do retries for about 40 min
                const maxNetworkRetries = (options.retry && options.retry.network && options.retry.network.times) ? options.retry.network.times : Ajax.defaults.NETWORK_ERROR_RETRY_COUNT;

                retry = (retryCount < maxNetworkRetries);

                // Network request timeout
                timeout = (options.retry && options.retry.network && options.retry.network.waitTimeBetweenRetries) ? options.retry.network.waitTimeBetweenRetries : Ajax.defaults.NETWORK_ERROR_RETRY_TIMEOUT;

            }

            if (retry) {
                // Trigger callback for retry
                options.onUploadRetry && options.onUploadRetry(retryCount + 1);

                // Set timeout for retry
                setTimeout(() => Ajax._fetch(url, method, options, resolve, reject, retryCount + 1), timeout);
            } else {
                let status = null;
                let message = null;

                if (error.response) {
                    if (error.response.status) {
                        status = error.response.status;
                    }
                }

                if (error.message) {
                    message = error.message;
                }

                // If not retrying, reject request
                reject({
                    message: "Unable to complete ajax request",
                    subMessage: message,
                    url: url,
                    method: method,
                    options: JSON.stringify(options),
                    status: status,
                    error: JSON.stringify(error),
                    retryCount: retryCount,
                    responseText: responseText
                });
            }
        });
};

/**
 * Validates that the given options is correct
 * Throws an error if not.
 * @param {Object} options
 * @param {string} method
 */
Ajax._validateOptions = (options, method) => {
    if (!options.hasOwnProperty("responseType")) {
        throw new Error(
            "responseType needs to be specified in the options object when using the Ajax lib. And it needs to be one of the following: " +
            Ajax.defaults.VALID_RESPONSE_TYPES.join(','));
    }
    if (Ajax.defaults.VALID_RESPONSE_TYPES.indexOf(options.responseType) < -1) {
        throw new Error("responseType needs to be one of " + Ajax.defaults.VALID_RESPONSE_TYPES.join(','));
    }

    if (Ajax.defaults.MANDATORY_BODY_METHODS.indexOf(method) > -1) {
        if (!options.hasOwnProperty("body")) {
            throw new Error(
                "POST and PUT requests must specify the body parameter in the options object"
            );
        }
    }
};

/**
 * Validate response status code and other validations
 * Throws an error if there's anything wrong
 *
 * @param {Response} response
 * @param {string|Object} options
 */
Ajax._validateResponse = (response, options) => {
    //Check if there is a regex to check if the response is invalid and we should try again
    if (options.retry && options.retry.regexMatchResponse && response.request
        .responseText.match &&
        response.request.responseText.match(options.retry.regexMatchResponse)
    ) {
        throw {
            message: "Response matched regex to try again",
            response: response,
            responseText: response.request.responseText,
            retry: true
        };
    }

    //Check if status code is ok
    if (response.status) {
        if (response.status > 399) {
            if (options.retry) {
                if (options.retry.statusCodes && options.retry.statusCodes.indexOf(
                        response.status) >= 0) {
                    throw {
                        message: "Unsuccessful ajax request. Got answer back from server but it had status code of: " +
                            response.status,
                        response: response,
                        retry: true
                    };
                }
            }
            throw {
                message: "Unsuccessful ajax request. Got answer back from server but it had status code of: " +
                    response.status,
                response: response,
                retry: false
            };
        }
    }
    else {
        throw {
            message: "Unsuccessful ajax request. Got answer back from server but it had no status code",
            response: response,
            retry: true
        };
    }
};

/**
 * Parse the response body into JSON or XML or whatever the user asked for
 * @param {string|Object} response
 * @param {Object} options
 * @param {Function} resolve
 */
Ajax._handleResponseData = (response, options, resolve) => {
    if (['JSON', 'BLOB', 'TEXT', 'NONE'].indexOf( options.responseType ) > -1) {
        resolve({
            data: response.data,
            response: response
        });
    } else if (options.responseType === 'XML') {
        if (!parseXml) {
            throw {
                message: "Unable to parse responseText into XML object because the parseXML lib could not be loaded",
                response: response,
                responseText: response.data
            };
        }
        try {
            var parsedXml = parseXml(response.data);
        } catch (error) {
            throw {
                message: "Unable to parse responseText into XML object",
                response: response,
                responseText: response.data,
                parseError: error
            };
        }

        //Check if the parsedXml contains error messages instead of the parsed xml
        if (parsedXml.getElementsByTagName('parsererror').length > 0) {
            throw {
                message: "Unable to parse responseText into XML object",
                response: response,
                responseText: response.data,
                parseError: (new XMLSerializer()).serializeToString(
                    parsedXml.getElementsByTagName('parsererror')[0])
            };
        }

        resolve({
            data: parsedXml,
            response: response
        });
    } else if (options.responseType === 'IMAGE') {
        try {
            const url = URL.createObjectURL(response.data);
        } catch (error) {
            throw {
                message: "Unable to convert response into an image ObjectUrl",
                response: response,
                responseText: response.data,
                parseError: error
            };
        }
        resolve({
            data: url,
            response: response
        });
    }
};

module.exports = Ajax;
